import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Advertiser, Geography } from '@shared/types';
import type { GeocodeResponse, MapConfigResponse, ZipBoundariesResponse, ZipBoundary } from '@shared/api';
import { buildMapboxGeoData, isZipIncluded } from '@shared/mapbox-geo';
import { uniqueBoundarySegments } from '@shared/geo-topology';
import { getGeocode, getMapConfig, getZipBoundaries } from '../../api/client';
import { geoExplicitCenter, geoQueryLabel } from '../../lib/geo';
import { DEFAULT_RADIUS_MILES, TILE_SIZE, fitZoom, milesToPixels, worldPixel } from '../../lib/mapFit';
import { GeoSchematic } from '../GeoSchematic';
import { PAGE_HEIGHT, PAGE_WIDTH } from '../Page';

/**
 * The full-bleed dark map background + real-geometry overlay for the
 * momentum geo_strategy page (design.md V8). Unlike MapVisual (a card-sized
 * slot whose ring is baked into the raster image), this owns an
 * INDEPENDENT SVG projection layer — the radius ring and real ZIP polygons
 * are drawn from lat/lng math, not from map pixels — so it keeps showing
 * genuine geometry even when the raster tier degrades (no token, no
 * provider, an image load failure). Only a fully-unresolved center (no
 * explicit coordinates and no usable geocode) falls all the way back to the
 * illustrative GeoSchematic, because at that point there is no real
 * coordinate to project anything from.
 *
 * Deliberately does NOT import from MapVisual.tsx or modify mapFit.ts — it
 * mirrors their caching/projection patterns locally so the standard family
 * and MapVisual's existing behavior stay byte-for-byte untouched.
 */

// ---------------------------------------------------------------------------
// Session-scoped caches (module state, mirrors MapVisual.tsx's own pattern).
// Momentum's own page tree never renders a MapVisual instance (only the
// standard family and momentum's now-removed geo map-card did), so keeping
// this cache local — rather than reaching into MapVisual's private module
// state, which isn't exported — costs nothing in practice while touching
// zero shared files.
// ---------------------------------------------------------------------------

let mapConfigPromise: Promise<MapConfigResponse> | null = null;
export function loadMapConfig(): Promise<MapConfigResponse> {
  mapConfigPromise ??= getMapConfig().catch((): MapConfigResponse => ({ provider: 'none' }));
  return mapConfigPromise;
}

const geocodeCache = new Map<string, Promise<GeocodeResponse>>();
export function loadGeocode(label: string): Promise<GeocodeResponse> {
  let pending = geocodeCache.get(label);
  if (!pending) {
    pending = getGeocode(label).catch(
      (err: unknown): GeocodeResponse => ({
        error: err instanceof Error ? err.message : String(err),
      }),
    );
    geocodeCache.set(label, pending);
  }
  return pending;
}

const zipCache = new Map<string, Promise<ZipBoundariesResponse>>();
function zipCacheKey(lat: number, lng: number, radiusMiles: number): string {
  return `${lat.toFixed(3)},${lng.toFixed(3)},${Math.round(radiusMiles)}`;
}
function loadZipBoundaries(lat: number, lng: number, radiusMiles: number): Promise<ZipBoundariesResponse> {
  const key = zipCacheKey(lat, lng, radiusMiles);
  let pending = zipCache.get(key);
  if (!pending) {
    pending = getZipBoundaries(lat, lng, radiusMiles).catch(
      (err: unknown): ZipBoundariesResponse => ({
        error: err instanceof Error ? err.message : String(err),
      }),
    );
    zipCache.set(key, pending);
  }
  return pending;
}

// ---------------------------------------------------------------------------
// Pure projection helpers, local to this file. Built entirely on mapFit.ts's
// EXISTING exports (worldPixel, TILE_SIZE, fitZoom, milesToPixels) — nothing
// here touches mapFit.ts, so MapVisual's own math is untouched.
// ---------------------------------------------------------------------------

/**
 * Keep the service area decisively left of center so the right-side narrative
 * can sit on a quiet map field without shrinking or clipping the geography.
 */
const BIAS_FRACTION_X = 0.16;

/**
 * Full-bleed OSM tiles need a bigger, non-square grid than MapVisual's 3x3
 * card cap. The grid is derived from the rendered box below so every edge is
 * covered at any pan offset.
 */
const MAPBOX_MAX_DIMENSION = 1280;
/** A ZIP label renders only when the polygon's projected bbox could hold it. */
const ZIP_LABEL_MIN_PX = 56;
/** …and never more than this many, largest footprints first. */
const ZIP_LABEL_CAP = 18;
const ZIP_LABEL_WIDTH = 50;
const ZIP_LABEL_HEIGHT = 24;

interface Point {
  x: number;
  y: number;
}

interface LatLng {
  lat: number;
  lng: number;
}

type MapAppearance = 'app' | 'light' | 'dark';
type ResolvedMapAppearance = Exclude<MapAppearance, 'app'>;

function rootAppearance(): ResolvedMapAppearance {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light';
}

function osmInteractiveStyle(theme: ResolvedMapAppearance): mapboxgl.StyleSpecification {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '© OpenStreetMap contributors',
      },
    },
    layers: [
      {
        id: 'map-background',
        type: 'background',
        paint: { 'background-color': theme === 'dark' ? '#071113' : '#e9eeee' },
      },
      {
        id: 'osm-base',
        type: 'raster',
        source: 'osm',
        paint: theme === 'dark'
          ? { 'raster-saturation': -0.82, 'raster-brightness-min': 0.08, 'raster-brightness-max': 0.42, 'raster-contrast': 0.22 }
          : { 'raster-saturation': 0, 'raster-brightness-min': 0, 'raster-brightness-max': 1, 'raster-contrast': 0 },
      },
    ],
  };
}

/** The map-center coordinate that puts `lat,lng` left of the box's true center by `biasFraction` (default ~16%, `BIAS_FRACTION_X`). */
function biasedMapCenter(
  lat: number,
  lng: number,
  zoom: number,
  boxWidthPx: number,
  biasFraction: number = BIAS_FRACTION_X,
): LatLng {
  const scale = TILE_SIZE * 2 ** zoom;
  const biasPx = biasFraction * boxWidthPx;
  return { lat, lng: lng + (biasPx / scale) * 360 };
}

/** Projects an arbitrary lat/lng into box-pixel space against a given map center + zoom. */
export function project(
  lat: number,
  lng: number,
  mapCenter: LatLng,
  zoom: number,
  boxWidthPx: number,
  boxHeightPx: number,
): Point {
  const centerWorld = worldPixel(mapCenter.lat, mapCenter.lng, zoom);
  const pointWorld = worldPixel(lat, lng, zoom);
  return {
    x: boxWidthPx / 2 + (pointWorld.x - centerWorld.x),
    y: boxHeightPx / 2 + (pointWorld.y - centerWorld.y),
  };
}

/** Inverse of `project`'s world-pixel step: a world pixel at `zoom` back to lat/lng. */
export function unprojectWorld(x: number, y: number, zoom: number): LatLng {
  const scale = TILE_SIZE * 2 ** zoom;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale))) * 180) / Math.PI;
  return { lat, lng: (x / scale) * 360 - 180 };
}

export interface FitPad {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Fit-to-bounds view: the (fractional) zoom and center at which the lat/lng
 * bounds fill the box minus `pad`, centered in the padded area. The padding
 * lets floating chrome (a title) sit on the map without covering the target.
 */
export function fitBoundsView(
  sw: LatLng,
  ne: LatLng,
  boxWidthPx: number,
  boxHeightPx: number,
  pad: FitPad,
): { center: LatLng; zoom: number } {
  const a = worldPixel(sw.lat, sw.lng, 0);
  const b = worldPixel(ne.lat, ne.lng, 0);
  const dx = Math.max(Math.abs(b.x - a.x), 1e-6);
  const dy = Math.max(Math.abs(a.y - b.y), 1e-6);
  const availW = Math.max(boxWidthPx - pad.left - pad.right, 40);
  const availH = Math.max(boxHeightPx - pad.top - pad.bottom, 40);
  const zoom = Math.min(17, Math.log2(Math.min(availW / dx, availH / dy)));
  const scale = 2 ** zoom;
  // Shift the map center so the bounds' midpoint lands mid-padded-area.
  const shiftX = (pad.left - pad.right) / 2;
  const shiftY = (pad.top - pad.bottom) / 2;
  const mx = ((a.x + b.x) / 2) * scale - shiftX;
  const my = ((a.y + b.y) / 2) * scale - shiftY;
  return { center: unprojectWorld(mx, my, zoom), zoom };
}

interface CanvasTile {
  tileX: number;
  tileY: number;
  zoom: number;
  left: number;
  top: number;
}

interface TileGrid {
  tiles: CanvasTile[];
  gridWidth: number;
  gridHeight: number;
  offsetX: number;
  offsetY: number;
}

/**
 * A rectangular (not forced-square) OSM tile grid sized to cover a full-bleed
 * box, centered on `mapCenter` at `zoom`. Mirrors mapFit.ts's own
 * `osmTileLayout` algorithm but without its 3x3 card-sized cap, and without
 * touching that file — see the module comment above.
 */
export function buildTileGrid(mapCenter: LatLng, zoom: number, boxWidthPx: number, boxHeightPx: number): TileGrid {
  const world = worldPixel(mapCenter.lat, mapCenter.lng, zoom);
  const centerTileX = Math.floor(world.x / TILE_SIZE);
  const centerTileY = Math.floor(world.y / TILE_SIZE);

  // One center tile plus a symmetric number on each side. Using the half
  // viewport directly guarantees the grid reaches both edges even when the
  // map center lies at the far edge of its center tile.
  const halfX = Math.ceil(boxWidthPx / (2 * TILE_SIZE));
  const halfY = Math.ceil(boxHeightPx / (2 * TILE_SIZE));

  const tileCount = 2 ** zoom;
  const tiles: CanvasTile[] = [];
  for (let dy = -halfY; dy <= halfY; dy++) {
    for (let dx = -halfX; dx <= halfX; dx++) {
      const rawX = centerTileX + dx;
      const tileX = ((rawX % tileCount) + tileCount) % tileCount;
      const tileY = Math.min(tileCount - 1, Math.max(0, centerTileY + dy));
      tiles.push({ tileX, tileY, zoom, left: (dx + halfX) * TILE_SIZE, top: (dy + halfY) * TILE_SIZE });
    }
  }

  const worldOriginX = (centerTileX - halfX) * TILE_SIZE;
  const worldOriginY = (centerTileY - halfY) * TILE_SIZE;

  return {
    tiles,
    gridWidth: (2 * halfX + 1) * TILE_SIZE,
    gridHeight: (2 * halfY + 1) * TILE_SIZE,
    offsetX: boxWidthPx / 2 - (world.x - worldOriginX),
    offsetY: boxHeightPx / 2 - (world.y - worldOriginY),
  };
}

function InteractiveGeoMap({
  provider, token, theme, center, businessCenter, zoom, radiusMiles, zips, geography, onToggleZip, allowNavigation,
}: {
  provider: 'mapbox' | 'osm'; token?: string; theme: ResolvedMapAppearance; center: LatLng; businessCenter: LatLng; zoom: number; radiusMiles: number | null; zips: ZipBoundary[]; geography: Geography; onToggleZip?: (next: string[]) => void; allowNavigation: boolean;
}): ReactElement {
  const mapNode = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [radiusOverlay, setRadiusOverlay] = useState({ x: 0, y: 0, radius: 0 });
  const latest = useRef({ zips, geography, onToggleZip, businessCenter, radiusMiles });
  latest.current = { zips, geography, onToggleZip, businessCenter, radiusMiles };

  const syncSources = (map: mapboxgl.Map | null) => {
    if (!map || !map.getSource('geo-zip-hit')) return;
    const data = buildMapboxGeoData(latest.current.zips, latest.current.geography);
    (map.getSource('geo-zip-hit') as mapboxgl.GeoJSONSource | undefined)?.setData(data.hitPolygons as Parameters<mapboxgl.GeoJSONSource['setData']>[0]);
  };

  const syncRadiusOverlay = (map: mapboxgl.Map | null) => {
    const current = latest.current;
    if (!map || !current.radiusMiles) {
      setRadiusOverlay({ x: 0, y: 0, radius: 0 });
      return;
    }
    const angularDistance = current.radiusMiles / 3958.8;
    const latitude = current.businessCenter.lat * Math.PI / 180;
    const northLatitude = Math.asin(
      Math.sin(latitude) * Math.cos(angularDistance)
      + Math.cos(latitude) * Math.sin(angularDistance),
    ) * 180 / Math.PI;
    const projectedCenter = map.project([current.businessCenter.lng, current.businessCenter.lat]);
    const projectedEdge = map.project([current.businessCenter.lng, northLatitude]);
    setRadiusOverlay({
      x: projectedCenter.x,
      y: projectedCenter.y,
      radius: Math.abs(projectedCenter.y - projectedEdge.y),
    });
  };

  useEffect(() => {
    const node = mapNode.current;
    if (!node) return undefined;
    if (token) mapboxgl.accessToken = token;
    const options: mapboxgl.MapboxOptions = {
      container: node,
      style: provider === 'mapbox'
        ? `mapbox://styles/mapbox/${theme === 'dark' ? 'dark-v11' : 'streets-v12'}`
        : osmInteractiveStyle(theme),
      center: [center.lng, center.lat],
      zoom,
      interactive: true,
      attributionControl: false,
      pitchWithRotate: false,
      dragRotate: false,
      touchZoomRotate: allowNavigation,
      keyboard: allowNavigation,
    };
    const map: mapboxgl.Map = provider === 'mapbox'
      ? new mapboxgl.Map(options)
      : new maplibregl.Map(options as unknown as maplibregl.MapOptions) as unknown as mapboxgl.Map;
    if (allowNavigation) {
      if (provider === 'mapbox') {
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
      } else {
        (map as unknown as maplibregl.Map).addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
      }
    } else {
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      map.dragPan.disable();
    }
    mapRef.current = map;
    const toggle = (event: mapboxgl.MapLayerMouseEvent) => {
      const zip = (event.features?.[0] as { properties?: Record<string, unknown> } | undefined)?.properties?.['zip'];
      if (typeof zip !== 'string' || !latest.current.onToggleZip) return;
      const current = latest.current;
      const selected = current.geography.zipCodes.length ? new Set(current.geography.zipCodes) : new Set(current.zips.filter((item) => item.included).map((item) => item.zip));
      selected.has(zip) ? selected.delete(zip) : selected.add(zip);
      current.onToggleZip?.([...selected]);
    };
    map.on('style.load', () => {
      map.addSource('geo-zip-hit', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: 'geo-zip-hit-area', type: 'fill', source: 'geo-zip-hit', paint: { 'fill-color': '#35c7c3', 'fill-opacity': 0.001 } });
      syncSources(map);
      syncRadiusOverlay(map);
      map.on('click', 'geo-zip-hit-area', toggle);
      map.on('mouseenter', 'geo-zip-hit-area', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'geo-zip-hit-area', () => { map.getCanvas().style.cursor = ''; });
    });
    map.on('move', () => syncRadiusOverlay(map));
    return () => { map.remove(); mapRef.current = null; };
  }, [provider, token, theme, allowNavigation]);

  useEffect(() => {
    mapRef.current?.jumpTo({ center: [center.lng, center.lat], zoom });
  }, [center.lat, center.lng, zoom]);

  useEffect(() => {
    const map = mapRef.current;
    syncSources(map);
    syncRadiusOverlay(map);
  }, [zips, geography, businessCenter.lat, businessCenter.lng, radiusMiles]);

  return (
    <>
      <div ref={mapNode} className="mtm-geo-mapbox" aria-label="Interactive service area map" />
      {radiusOverlay.radius > 0 ? (
        <svg className="mtm-geo-radius-overlay" aria-hidden="true">
          <circle
            cx={radiusOverlay.x}
            cy={radiusOverlay.y}
            r={radiusOverlay.radius}
            fill={theme === 'dark' ? 'rgba(53, 199, 195, 0.08)' : 'rgba(53, 199, 195, 0.13)'}
            stroke={theme === 'dark' ? '#63dfdb' : '#087f7b'}
            strokeWidth="2.1"
          />
        </svg>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// GeoMapCanvas
// ---------------------------------------------------------------------------

export interface GeoMapCanvasProps {
  advertiser: Advertiser;
  geography: Geography;
  readOnly: boolean;
  className?: string;
  /** Fires whenever the fetched ZIP set changes (including to null on error/unavailable/no center). */
  onZipsChange?: (zips: ZipBoundary[] | null) => void;
  /**
   * A ZIP polygon was clicked (only wired up when !readOnly): the next full
   * `zipCodes` list per the V8 include rule — first toggle materializes the
   * current auto (distance-based) set, then flips the clicked ZIP.
   */
  onToggleZip?: (nextZipCodes: string[]) => void;
  /** Enables ordinary pan, zoom, and keyboard navigation outside the fixed proposal-slide canvas. */
  allowNavigation?: boolean;
  /** Proposal pages stay dark by default; editable workspace maps can follow the app chrome. */
  appearance?: MapAppearance;
  /**
   * Overrides how far left of the box's true center the business point sits
   * (see `BIAS_FRACTION_X`/`biasedMapCenter` above), as a 0-1 fraction of the
   * box width. momentum_v1's own geo_strategy page never passes this (stays
   * at the existing ~16% default); momentum_v2's full-bleed geo slide (design.md
   * V15, reference/proposal-template-brief-v2.md §4 `08 geo`: "offset the map
   * centre ~12% right of card centre") passes a smaller value so the radius
   * ring lands in the open area left of its own, smaller bottom-right text
   * card instead of a full-height right column.
   */
  centerBiasFraction?: number;
}

export function GeoMapCanvas({
  advertiser,
  geography,
  readOnly,
  className,
  onZipsChange,
  onToggleZip,
  allowNavigation = false,
  appearance = 'dark',
  centerBiasFraction,
}: GeoMapCanvasProps): ReactElement {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: PAGE_WIDTH, height: PAGE_HEIGHT });
  const [mapConfig, setMapConfig] = useState<MapConfigResponse | null>(null);
  const [resolvedCenter, setResolvedCenter] = useState<LatLng | null>(
    geoExplicitCenter(advertiser, geography) ?? null,
  );
  const [imgFailed, setImgFailed] = useState(false);
  const [zips, setZips] = useState<ZipBoundary[] | null>(null);
  const [appAppearance, setAppAppearance] = useState<ResolvedMapAppearance>(rootAppearance);

  useEffect(() => {
    if (appearance !== 'app' || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const update = () => setAppAppearance(rootAppearance());
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [appearance]);

  const mapAppearance: ResolvedMapAppearance = appearance === 'app' ? appAppearance : appearance;

  const explicitCenter = geoExplicitCenter(advertiser, geography);
  const label = geoQueryLabel(advertiser);
  const businessLabel = advertiser.name?.trim() || 'This location';
  const configuredRadius = geography.storeRadiusMiles;
  const fetchRadiusMiles = configuredRadius ?? DEFAULT_RADIUS_MILES;

  const onZipsChangeRef = useRef(onZipsChange);
  useEffect(() => {
    onZipsChangeRef.current = onZipsChange;
  });

  // Design-space box measurement (mirrors MapVisual — the page canvas is a
  // fixed 1920x1080 scaled via CSS transform, which clientWidth/Height
  // otherwise ignore).
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 4 && rect.height > 4) {
        setBox({ width: rect.width, height: rect.height });
      }
    });
    observer.observe(el);
    if (el.clientWidth > 4 && el.clientHeight > 4) {
      setBox({ width: el.clientWidth, height: el.clientHeight });
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    void loadMapConfig().then((cfg) => {
      if (live) setMapConfig(cfg);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setImgFailed(false);
    if (explicitCenter) {
      setResolvedCenter(explicitCenter);
      return undefined;
    }
    const query = label?.trim();
    if (!query) {
      setResolvedCenter(null);
      return undefined;
    }
    let live = true;
    setResolvedCenter(null);
    void loadGeocode(query).then((res) => {
      if (!live) return;
      if ('error' in res) {
        setResolvedCenter(null);
        return;
      }
      setResolvedCenter({ lat: res.lat, lng: res.lng });
    });
    return () => {
      live = false;
    };
  }, [explicitCenter?.lat, explicitCenter?.lng, label]);

  useEffect(() => {
    if (!resolvedCenter) {
      setZips(null);
      onZipsChangeRef.current?.(null);
      return undefined;
    }
    let live = true;
    void loadZipBoundaries(resolvedCenter.lat, resolvedCenter.lng, fetchRadiusMiles).then((res) => {
      if (!live) return;
      if ('error' in res) {
        setZips(null);
        onZipsChangeRef.current?.(null);
        return;
      }
      setZips(res.zips);
      onZipsChangeRef.current?.(res.zips);
    });
    return () => {
      live = false;
    };
  }, [resolvedCenter?.lat, resolvedCenter?.lng, fetchRadiusMiles]);

  // No coordinate to project anything from — the true final fallback.
  if (!resolvedCenter) {
    return (
      <div ref={boxRef} className={`mtm-geo-canvas mtm-geo-canvas-empty${className ? ` ${className}` : ''}`}>
        <GeoSchematic geography={geography} centerLabel={businessLabel} />
      </div>
    );
  }

  const { lat, lng } = resolvedCenter;

  // "Fit zoom to the radius (default 25 when null; when radius is null but
  // ZIPs exist, fit to the included centroid extent)" (design.md V8 #1).
  let fitRadiusMiles = DEFAULT_RADIUS_MILES;
  if (typeof configuredRadius === 'number') {
    fitRadiusMiles = configuredRadius;
  } else if (geography.zipCodes.length > 0 && zips) {
    const selected = zips.filter((z) => geography.zipCodes.includes(z.zip));
    if (selected.length > 0) {
      fitRadiusMiles = Math.max(...selected.map((z) => z.distanceMiles));
    }
  }

  const fittedZoom = fitZoom(fitRadiusMiles, lat, box.width, box.height);
  const zoom = !readOnly && allowNavigation ? Math.max(1, fittedZoom - 1) : fittedZoom;
  // The editable workspace keeps the service radius centered like an ordinary
  // map control. Proposal slides retain the intentional left bias for copy.
  const mapCenter = !readOnly && allowNavigation
    ? { lat, lng }
    : biasedMapCenter(lat, lng, zoom, box.width, centerBiasFraction);
  const provider = mapConfig?.provider ?? 'none';

  let background: ReactElement | null = null;
  if (provider === 'mapbox' && mapConfig?.mapboxToken && !imgFailed) {
    const scale = Math.min(1, MAPBOX_MAX_DIMENSION / box.width, MAPBOX_MAX_DIMENSION / box.height);
    const reqWidth = Math.max(1, Math.round(box.width * scale));
    const reqHeight = Math.max(1, Math.round(box.height * scale));
    const url =
      `https://api.mapbox.com/styles/v1/mapbox/${mapAppearance === 'dark' ? 'dark-v11' : 'light-v11'}/static/` +
      `${mapCenter.lng},${mapCenter.lat},${zoom}/${reqWidth}x${reqHeight}@2x` +
      `?access_token=${encodeURIComponent(mapConfig.mapboxToken)}`;
    background = (
      <img
        className="mtm-geo-bg-image"
        src={url}
        width={box.width}
        height={box.height}
        alt=""
        onError={() => setImgFailed(true)}
      />
    );
  } else if (provider === 'osm' && !imgFailed) {
    const grid = buildTileGrid(mapCenter, zoom, box.width, box.height);
    background = (
      <div
        className="mtm-geo-bg-tiles"
        style={{ left: grid.offsetX, top: grid.offsetY, width: grid.gridWidth, height: grid.gridHeight }}
      >
        {grid.tiles.map((tile) => (
          <img
            key={`${tile.zoom}/${tile.tileX}/${tile.tileY}`}
            className="mtm-geo-bg-tile"
            src={`https://tile.openstreetmap.org/${tile.zoom}/${tile.tileX}/${tile.tileY}.png`}
            width={TILE_SIZE}
            height={TILE_SIZE}
            style={{ left: tile.left, top: tile.top }}
            alt=""
            onError={() => setImgFailed(true)}
          />
        ))}
      </div>
    );
  }

  const businessPx = project(lat, lng, mapCenter, zoom, box.width, box.height);
  const ringRadiusPx =
    typeof configuredRadius === 'number'
      ? Math.max(24, Math.min(milesToPixels(configuredRadius, zoom, lat), Math.min(box.width, box.height) / 2 - 24))
      : 0;

  const handlePolygonClick = (zip: ZipBoundary) => {
    if (readOnly || !onToggleZip || !zips) return;
    const currentIncluded =
      geography.zipCodes.length > 0
        ? new Set(geography.zipCodes)
        : new Set(zips.filter((z) => z.included).map((z) => z.zip));
    if (currentIncluded.has(zip.zip)) currentIncluded.delete(zip.zip);
    else currentIncluded.add(zip.zip);
    onToggleZip(Array.from(currentIncluded));
  };

  const usesInteractiveMap = !readOnly && allowNavigation && (
    provider === 'osm' || (provider === 'mapbox' && Boolean(mapConfig?.mapboxToken))
  );

  // Collision-aware label thinning. A dense metro radius can include 50+
  // ZCTAs, so only labels with enough footprint and no collision with a prior
  // label or the business callout survive. This mirrors a symbol layer's
  // placement behavior while retaining the SVG fallback/export path.
  const labeledZips = new Set<string>();
  if (zips) {
    const occupied = [{ left: businessPx.x + 10, top: businessPx.y - 44, right: businessPx.x + 270, bottom: businessPx.y + 30 }];
    zips
      .filter((z) => isZipIncluded(z, geography))
      .map((z) => {
        let minLng = Infinity;
        let maxLng = -Infinity;
        let minLat = Infinity;
        let maxLat = -Infinity;
        for (const ring of z.rings) {
          for (const [rLng, rLat] of ring) {
            if (rLng < minLng) minLng = rLng;
            if (rLng > maxLng) maxLng = rLng;
            if (rLat < minLat) minLat = rLat;
            if (rLat > maxLat) maxLat = rLat;
          }
        }
        const a = project(minLat, minLng, mapCenter, zoom, box.width, box.height);
        const b = project(maxLat, maxLng, mapCenter, zoom, box.width, box.height);
        return { zip: z.zip, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
      })
      .filter((c) => Math.min(c.w, c.h) >= ZIP_LABEL_MIN_PX)
      .sort((a, b) => b.w * b.h - a.w * a.h)
      .forEach((candidate) => {
        if (labeledZips.size >= ZIP_LABEL_CAP) return;
        const zip = zips.find((item) => item.zip === candidate.zip);
        if (!zip) return;
        const center = project(zip.centroid.lat, zip.centroid.lng, mapCenter, zoom, box.width, box.height);
        const labelBox = {
          left: center.x - ZIP_LABEL_WIDTH / 2,
          top: center.y - ZIP_LABEL_HEIGHT / 2,
          right: center.x + ZIP_LABEL_WIDTH / 2,
          bottom: center.y + ZIP_LABEL_HEIGHT / 2,
        };
        const collides = occupied.some(
          (taken) => labelBox.left < taken.right && labelBox.right > taken.left && labelBox.top < taken.bottom && labelBox.bottom > taken.top,
        );
        if (!collides) {
          labeledZips.add(candidate.zip);
          occupied.push(labelBox);
        }
      });
  }

  const boundarySegments = zips ? uniqueBoundarySegments(zips.filter((zip) => isZipIncluded(zip, geography)).flatMap((zip) => zip.rings)) : [];

  return (
    <div
      ref={boxRef}
      className={`mtm-geo-canvas${className ? ` ${className}` : ''}`}
      data-map-theme={mapAppearance}
      style={{ background: mapAppearance === 'dark' ? '#071113' : '#e9eeee' }}
    >
      {usesInteractiveMap && (provider === 'osm' || mapConfig?.mapboxToken) ? (
        <InteractiveGeoMap
          provider={provider}
          token={mapConfig?.mapboxToken}
          theme={mapAppearance}
          center={mapCenter}
          businessCenter={{ lat, lng }}
          zoom={zoom}
          radiusMiles={configuredRadius ?? DEFAULT_RADIUS_MILES}
          zips={zips ?? []}
          geography={geography}
          onToggleZip={onToggleZip}
          allowNavigation={allowNavigation}
        />
      ) : background}
      {!usesInteractiveMap ? <svg
        className={`mtm-geo-svg-overlay${!readOnly ? ' mtm-geo-svg-editable' : ''}`}
        viewBox={`0 0 ${box.width} ${box.height}`}
        width={box.width}
        height={box.height}
        role="img"
        aria-label={`Service area map around ${businessLabel}`}
      >
        {zips
          ? zips.map((zip) => {
              const included = isZipIncluded(zip, geography);
              return (
                <g
                  key={zip.zip}
                  className={`mtm-geo-zip${included ? ' mtm-geo-zip-included' : ' mtm-geo-zip-muted'}`}
                  onClick={!readOnly ? () => handlePolygonClick(zip) : undefined}
                >
                  <title>{zip.zip}</title>
                  {zip.rings.map((ring, ringIndex) => (
                    <polygon
                      key={ringIndex}
                      className="mtm-geo-zip-poly"
                      points={ring.map(([zLng, zLat]) => {
                        const p = project(zLat, zLng, mapCenter, zoom, box.width, box.height);
                        return `${p.x},${p.y}`;
                      }).join(' ')}
                    />
                  ))}
                  {included && labeledZips.has(zip.zip)
                    ? (() => {
                        const c = project(zip.centroid.lat, zip.centroid.lng, mapCenter, zoom, box.width, box.height);
                        return (
                          <text x={c.x} y={c.y} className="mtm-geo-zip-label" textAnchor="middle">
                            {zip.zip}
                          </text>
                        );
                      })()
                    : null}
                </g>
              );
            })
          : null}

        <g className="mtm-geo-zip-boundaries" pointerEvents="none">
          {boundarySegments.map((segment) => {
            const start = project(segment.start[1], segment.start[0], mapCenter, zoom, box.width, box.height);
            const end = project(segment.end[1], segment.end[0], mapCenter, zoom, box.width, box.height);
            return <line key={segment.key} x1={start.x} y1={start.y} x2={end.x} y2={end.y} />;
          })}
        </g>

        {typeof configuredRadius === 'number' ? (
          <g className="mtm-geo-ring">
            <circle cx={businessPx.x} cy={businessPx.y} r={ringRadiusPx} className="mtm-geo-ring-circle" />
          </g>
        ) : null}
        <g className="mtm-geo-center"><circle cx={businessPx.x} cy={businessPx.y} r={7} className="mtm-geo-ring-center" /></g>
      </svg> : null}
      {!usesInteractiveMap ? <div className="mtm-geo-business" style={{ left: businessPx.x, top: businessPx.y }}>
        <svg className="mtm-geo-business-pin" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 22s7-6.45 7-13a7 7 0 1 0-14 0c0 6.55 7 13 7 13Z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
        <div className="mtm-geo-business-copy">
          <strong>{businessLabel}</strong>
          {typeof configuredRadius === 'number' ? <span>{configuredRadius} mi radius</span> : null}
        </div>
      </div> : null}
      <div className="mtm-geo-vignette" aria-hidden="true" />
    </div>
  );
}
